import { useState } from 'react'
import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { type Route } from './+types/support.ts'

export const meta: Route.MetaFunction = () => {
	return [
		{ title: 'Support - MyBillingsMethod' },
		{
			name: 'description',
			content:
				'Get help with MyBillingsMethod. Find answers to common questions about the Billings Method tracker, privacy, subscriptions, and technical support.',
		},
	]
}

export default function SupportRoute() {
	return (
		<div className="bg-white">
			{/* Navigation */}
			<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
				<div className="mx-auto max-w-5xl px-6 py-4">
					<div className="flex items-center justify-between">
						<Link to="/" className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
							<span className="text-lg font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</Link>
						<Link
							to="/"
							className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							<Icon name="arrow-left" className="mr-2 h-4 w-4" />
							Back to Home
						</Link>
					</div>
				</div>
			</nav>

			{/* Header */}
			<div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
				<div className="mx-auto max-w-4xl px-6 py-16">
					<h1 className="mb-4 text-4xl font-bold">Support</h1>
					<p className="text-lg text-purple-100">
						We're here to help. Find answers below or email us.
					</p>
				</div>
			</div>

			{/* Content */}
			<div className="mx-auto max-w-4xl px-6 py-12">
				{/* Pre-Launch Notice */}
				<div className="mb-12 rounded-xl border-2 border-purple-200 bg-purple-50 p-8">
					<h2 className="mb-4 flex items-center text-2xl font-bold text-purple-900">
						<Icon name="calendar" className="mr-3 h-8 w-8" />
						Launching April 2026
					</h2>
					<p className="mb-4 text-lg text-gray-700">
						MyBillingsMethod is currently in pre-launch. The app is not yet
						available for download.
					</p>
					<p className="text-gray-600">
						<strong>Beta access:</strong> First 100 users can reserve beta
						access at £29/year (normally £39/year). Beta users get early access
						and lock in this rate forever.
					</p>
				</div>

				{/* Quick Contact */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						Need Help? Contact Us
					</h2>
					<div className="rounded-lg bg-gray-50 p-6">
						<p className="mb-4 text-gray-700">
							Have a question not answered below? We typically respond within 24
							hours.
						</p>
						<div className="space-y-2">
							<p className="text-gray-900">
								<strong>Email:</strong>{' '}
								<a
									href="mailto:hello@mybillingsmethod.com"
									className="text-purple-600 underline hover:text-purple-700"
								>
									hello@mybillingsmethod.com
								</a>
							</p>
							<p className="text-gray-900">
								<strong>Company:</strong> CHRIDAM LTD
							</p>
							<p className="text-gray-900">
								<strong>Location:</strong> United Kingdom
							</p>
						</div>
					</div>
				</section>

				{/* FAQ Accordion */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						Frequently Asked Questions
					</h2>
					<FAQAccordion />
				</section>

				{/* Getting Started (Post-Launch) */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						Getting Started
					</h2>
					<div className="space-y-4 text-gray-700">
						<p>
							<strong>When the app launches (April 2026):</strong>
						</p>
						<ol className="ml-6 space-y-3">
							<li className="flex items-start">
								<span className="mr-3 font-bold text-purple-600">1.</span>
								<span>
									Download the Mac app from the link we email you
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3 font-bold text-purple-600">2.</span>
								<span>
									Log in with your email and the password you created
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3 font-bold text-purple-600">3.</span>
								<span>
									Start tracking your observations - the app guides you through
									the process
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-3 font-bold text-purple-600">4.</span>
								<span>
									If you're new to the Billings Method, we recommend consulting a
									certified instructor (see below)
								</span>
							</li>
						</ol>
					</div>
				</section>

				{/* Billings Method Instructors */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						Find a Billings Method Instructor
					</h2>
					<div className="space-y-4 text-gray-700">
						<p>
							MyBillingsMethod is a tracking tool, not a replacement for proper
							instruction. We strongly recommend learning the Billings Method
							from a certified instructor.
						</p>

						<div className="space-y-4">
							<div className="rounded-lg border-l-4 border-purple-600 bg-gray-50 p-6">
								<h3 className="mb-2 font-semibold text-gray-900">
									BOMA UK (Billings Ovulation Method Association)
								</h3>
								<p className="mb-2 text-sm text-gray-600">
									Find certified instructors in the United Kingdom
								</p>
								<a
									href="https://www.fertilityuk.org"
									target="_blank"
									rel="noopener noreferrer"
									className="text-purple-600 underline hover:text-purple-700"
								>
									fertilityuk.org →
								</a>
							</div>

							<div className="rounded-lg border-l-4 border-purple-600 bg-gray-50 p-6">
								<h3 className="mb-2 font-semibold text-gray-900">
									WOOMB International
								</h3>
								<p className="mb-2 text-sm text-gray-600">
									World Organisation Ovulation Method Billings - Find
									instructors worldwide
								</p>
								<a
									href="https://woombinternational.org"
									target="_blank"
									rel="noopener noreferrer"
									className="text-purple-600 underline hover:text-purple-700"
								>
									woomb.org →
								</a>
							</div>
						</div>
					</div>
				</section>

				{/* Troubleshooting (Post-Launch) */}
				<section className="mb-12">
					<h2 className="mb-6 text-3xl font-bold text-gray-900">
						Troubleshooting
					</h2>
					<div className="space-y-4">
						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Mac Requirements
							</h3>
							<ul className="ml-6 space-y-2 text-gray-700">
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>macOS 12.0 (Monterey) or later</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>Apple Silicon (M1/M2/M3) or Intel processor</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>200 MB available storage</span>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="mb-2 text-lg font-semibold text-gray-900">
								Common Issues
							</h3>
							<p className="text-gray-700">
								If you encounter technical issues after launch, please email us
								at{' '}
								<a
									href="mailto:hello@mybillingsmethod.com"
									className="text-purple-600 underline hover:text-purple-700"
								>
									hello@mybillingsmethod.com
								</a>{' '}
								with:
							</p>
							<ul className="ml-6 mt-2 space-y-2 text-gray-700">
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>Description of the issue</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>Your macOS version</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>Steps to reproduce the problem</span>
								</li>
								<li className="flex items-start">
									<span className="mr-2 text-purple-600">•</span>
									<span>Screenshots if applicable</span>
								</li>
							</ul>
						</div>
					</div>
				</section>

				{/* Final CTA */}
				<div className="rounded-xl bg-purple-50 p-8 text-center">
					<h2 className="mb-4 text-2xl font-bold text-gray-900">
						Still have questions?
					</h2>
					<p className="mb-6 text-gray-700">
						We're here to help. Email us anytime.
					</p>
					<a
						href="mailto:hello@mybillingsmethod.com"
						className="inline-flex items-center rounded-lg bg-purple-600 px-6 py-3 font-semibold text-white hover:bg-purple-700"
					>
						<Icon name="envelope-closed" className="mr-2 h-5 w-5" />
						Contact Support
					</a>
				</div>
			</div>

			{/* Footer */}
			<footer className="border-t border-gray-200 bg-white py-12">
				<div className="mx-auto max-w-5xl px-6">
					<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
						<div className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
							<span className="font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</div>
						<div className="flex gap-6">
							<Link to="/privacy" className="hover:text-gray-900">
								Privacy
							</Link>
							<Link to="/tos" className="hover:text-gray-900">
								Terms
							</Link>
							<Link to="/support" className="hover:text-gray-900">
								Support
							</Link>
						</div>
					</div>
					<p className="mt-6 text-center text-xs text-gray-500">
						© {new Date().getFullYear()} MyBillingsMethod. Your data is yours.
						Always local. Always private.
					</p>
				</div>
			</footer>
		</div>
	)
}

function FAQAccordion() {
	const [openIndex, setOpenIndex] = useState<number | null>(null)

	const faqs = [
		{
			question: 'What is the Billings Method?',
			answer:
				"The Billings Ovulation Method is a natural family planning method based on observing cervical mucus patterns to identify fertile and infertile days in a woman's cycle. It requires no devices or thermometers - just daily observations of your body's natural signs.",
		},
		{
			question: 'Is my data really private?',
			answer:
				'Yes. Absolutely. Your fertility observations, cycle data, and health information are stored exclusively in an encrypted database on your Mac. This data NEVER syncs to cloud servers. We physically cannot access your fertility data because it never leaves your device. This is privacy by architecture, not just a promise.',
		},
		{
			question: 'Do I need to be trained in the Billings Method first?',
			answer:
				'We strongly recommend it. MyBillingsMethod is a tracking tool that helps you record and understand your observations, but it cannot replace proper instruction from a certified Billings Method instructor. Links to find instructors are available on this page.',
		},
		{
			question: 'What is the difference between the free and paid plans?',
			answer:
				'The free "Try It Free" plan includes basic charting functionality. The Practitioner plan (£39/year, or £29 for beta users) adds AI-powered pattern recognition, instructor exports, and advanced features. The Couple plan (£59/year) includes everything in Practitioner plus partner features for shared tracking.',
		},
		{
			question: 'Can I cancel my subscription?',
			answer:
				'Yes, anytime. You can cancel from your account settings. Cancellation takes effect at the end of your current billing period, and you retain access until then. If you reserved beta access before launch (April 2026), you can get a full refund anytime before launch. After launch, refunds are available within 30 days of purchase.',
		},
		{
			question: 'Will my beta pricing (£29/year) ever increase?',
			answer:
				'No. The first 100 users who secure beta pricing at £29/year lock in that rate forever. As long as you maintain your subscription, your price will never increase. This is our thank you for supporting us early.',
		},
		{
			question: 'Does the app work offline?',
			answer:
				'Yes, completely. Since all your fertility data is stored locally on your Mac, the app works fully offline. You only need internet for initial download, login, and subscription management.',
		},
		{
			question: 'What does the AI pattern recognition do?',
			answer:
				'The AI (available on Practitioner and Couple plans) runs locally on your Mac and helps identify patterns in your cycle data. It can highlight recurring patterns, unusual observations, and provide educational insights. However, the AI never overrides the Billings Method rules - you remain in control of all fertility decisions.',
		},
		{
			question: 'Can I export my data to share with my instructor?',
			answer:
				'Yes. The Practitioner and Couple plans include export functionality. You can export your charts to PDF format to share with your Billings Method instructor during consultations.',
		},
		{
			question: 'Is there a Windows or iPhone version?',
			answer:
				'Not currently. MyBillingsMethod is Mac-only at launch (April 2026). We chose to focus on one platform first to deliver the best possible experience. Future platform support will depend on user demand.',
		},
	]

	return (
		<div className="space-y-4">
			{faqs.map((faq, index) => (
				<FAQItem
					key={index}
					question={faq.question}
					answer={faq.answer}
					isOpen={openIndex === index}
					onToggle={() => setOpenIndex(openIndex === index ? null : index)}
				/>
			))}
		</div>
	)
}

function FAQItem({
	question,
	answer,
	isOpen,
	onToggle,
}: {
	question: string
	answer: string
	isOpen: boolean
	onToggle: () => void
}) {
	return (
		<div className="rounded-lg border border-gray-200 bg-white">
			<button
				onClick={onToggle}
				className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50"
			>
				<span className="pr-8 font-semibold text-gray-900">{question}</span>
				<Icon
					name={isOpen ? 'chevron-up' : 'chevron-down'}
					className="h-5 w-5 flex-shrink-0 text-purple-600"
				/>
			</button>
			{isOpen && (
				<div className="border-t border-gray-200 px-6 py-4 text-gray-700">
					{answer}
				</div>
			)}
		</div>
	)
}
